import type { Cell } from "../engine/engine";

interface RlePattern {
  readonly cells: readonly Cell[];
  readonly width: number;
  readonly height: number;
}

function assertSupportedRule(rule: string | undefined): void {
  if (!rule) {
    return;
  }

  const normalized = rule.toUpperCase().replaceAll(/\s/g, "");
  if (normalized !== "B3/S23" && normalized !== "S23/B3") {
    throw new Error(`Unsupported rule "${rule}". Expected Conway's B3/S23.`);
  }
}

function assertDimension(value: number, name: string): void {
  if (!Number.isSafeInteger(value) || value <= 0) {
    throw new Error(`${name} must be a positive safe integer.`);
  }
}

export function parseRle(source: string): RlePattern {
  const lines = source.replaceAll("\r\n", "\n").split("\n");
  const headerIndex = lines.findIndex((line) =>
    /^\s*x\s*=\s*\d+\s*,\s*y\s*=\s*\d+/i.test(line),
  );
  if (headerIndex < 0) {
    throw new Error("RLE header is missing.");
  }

  const header = lines[headerIndex] ?? "";
  const match = header.match(
    /^\s*x\s*=\s*(\d+)\s*,\s*y\s*=\s*(\d+)(?:\s*,\s*rule\s*=\s*([^,\s]+))?/i,
  );
  if (!match) {
    throw new Error("RLE header is invalid.");
  }

  const width = Number(match[1]);
  const height = Number(match[2]);
  assertDimension(width, "RLE width");
  assertDimension(height, "RLE height");
  assertSupportedRule(match[3]);
  const body = lines
    .slice(headerIndex + 1)
    .filter((line) => !line.trimStart().startsWith("#"))
    .join("")
    .replaceAll(/\s/g, "");

  const cells: Cell[] = [];
  let x = 0;
  let y = 0;
  let countText = "";
  let terminated = false;

  for (const character of body) {
    if (/\d/.test(character)) {
      countText += character;
      continue;
    }

    const count = countText === "" ? 1 : Number(countText);
    countText = "";
    if (!Number.isSafeInteger(count) || count <= 0) {
      throw new Error("RLE contains an invalid run length.");
    }

    switch (character.toLowerCase()) {
      case "b":
        x += count;
        break;
      case "o":
        if (x + count > width || y >= height) {
          throw new Error("RLE data exceeds its declared bounds.");
        }
        for (let offset = 0; offset < count; offset += 1) {
          cells.push({ x: x + offset, y });
        }
        x += count;
        break;
      case "$":
        y += count;
        x = 0;
        break;
      case "!":
        terminated = true;
        break;
      default:
        throw new Error(`Unexpected RLE token "${character}".`);
    }

    if (terminated) {
      break;
    }
    if (x > width || y >= height) {
      throw new Error("RLE data exceeds its declared bounds.");
    }
  }

  if (!terminated) {
    throw new Error("RLE terminator is missing.");
  }
  return { cells, width, height };
}

export function centerCells(
  cells: readonly Cell[],
  patternWidth: number,
  patternHeight: number,
  targetWidth: number,
  targetHeight: number,
  horizontalCenter = 0.5,
): Cell[] {
  if (horizontalCenter < 0 || horizontalCenter > 1) {
    throw new RangeError("horizontalCenter must be between 0 and 1.");
  }
  const xOffset = Math.floor(
    targetWidth * horizontalCenter - patternWidth / 2,
  );
  const yOffset = Math.floor((targetHeight - patternHeight) / 2);
  return cells
    .map(({ x, y }) => ({ x: x + xOffset, y: y + yOffset }))
    .filter(
      ({ x, y }) =>
        x >= 0 && x < targetWidth && y >= 0 && y < targetHeight,
    );
}
